import { SearchIcon } from "@heroicons/react/solid";
import { getDefaultFilter, useOne, useTranslation } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import Select from "components/select";
import { endpointService } from "components/shared/services/endpointService";
import { IConditionIdsItem, IEventIdsItem } from "interfaces";
import { isEmpty } from "lodash";
import { useState } from "react";
import { STATUS_OPTIONS } from "utills/constants";

export const PromotionCouponFilter = ({
  filters,
  setFilters,
  events,
}: {
  filters: any;
  setFilters: any;
  events: IEventIdsItem[];
}) => {
  const { translate } = useTranslation();
  const [keyword, setKeyword] = useState(
    isEmpty(getDefaultFilter("q", filters, "contains"))
      ? ""
      : getDefaultFilter("q", filters, "contains")
  );
  const [eventId, setEventId] = useState(
    isEmpty(getDefaultFilter("event_id", filters, "eq"))
      ? ""
      : getDefaultFilter("event_id", filters, "eq")
  );
  const [isActive, setIsActive] = useState(
    isEmpty(getDefaultFilter("is_active", filters, "eq"))
      ? ""
      : getDefaultFilter("is_active", filters, "eq")
  );
  const [conditionId, setConditionId] = useState(
    isEmpty(getDefaultFilter("rule_id", filters, "eq"))
      ? ""
      : getDefaultFilter("rule_id", filters, "eq")
  );

  const { data: conditionData, isFetching: isFetchingConditionIds } = useOne<
    IConditionIdsItem[]
  >({
    resource: endpointService.getPromotionEndPoint("getConditionDropdown2", {
      eventId: eventId || "all",
    }),
    id: "",
    meta: {
      resource: "data",
    },
  });

  const conditionIds = (conditionData?.data ?? []).map(
    (condition: IConditionIdsItem) => ({
      value: condition.id,
      label: condition.note,
    })
  );

  const eventIds = (events ?? []).map(
    (event: { id: string; name_en: string }) => ({
      value: event.id,
      label: event.name_en,
    })
  );

  const _handleSubmitFilter = () => {
    setFilters([
      { field: "q", operator: "contains", value: keyword },
      { field: "event_id", operator: "eq", value: eventId },
      { field: "is_active", operator: "eq", value: isActive },
      { field: "rule_id", operator: "eq", value: conditionId },
      { field: "page", operator: "eq", value: 1 },
    ]);
  };

  return (
    <div className="w-full flex flex-col gap-y-4">
      <div className="w-full grid gap-y-4 sm:grid-cols-6 sm:items-end sm:gap-x-3">
        <div className="sm:col-span-2">
          <label htmlFor="q" className="block text-sm font-medium leading-6">
            {translate("search")}
          </label>
          <div className="mt-2">
            <Input
              className="px-2 py-2"
              label="Search"
              id="q"
              type="text"
              placeholder={translate("placeholder-coupon-code")}
              value={keyword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setKeyword(e.target.value)
              }
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  _handleSubmitFilter();
                }
              }}
            />
          </div>
        </div>
        <div className="sm:col-span-4">
          <label
            htmlFor="event_id"
            className="block text-sm font-medium leading-6"
          >
            {translate("event-name")}
          </label>
          <div className="mt-2">
            <Select
              options={eventIds}
              isClearable={true}
              id="event_id"
              placeholder={translate("select-event")}
              className="py-0.5"
              value={eventIds.find((d) => d.value === eventId)}
              onChange={(selected: { label: string; value: string }) => {
                setEventId(selected?.value);
                setConditionId("");
              }}
            />
          </div>
        </div>
      </div>
      <div className="w-full grid gap-y-4 sm:grid-cols-6 sm:items-end sm:gap-x-3">
        <div className="sm:col-span-2">
          <label
            htmlFor="is_active"
            className="block text-sm font-medium leading-6"
          >
            {translate("status")}
          </label>
          <div className="mt-2">
            <Select
              options={STATUS_OPTIONS}
              isClearable={true}
              id="is_active"
              placeholder={translate("select-status")}
              className="py-0.5"
              value={STATUS_OPTIONS.find((d) => d.value === isActive)}
              onChange={(selected: { label: string; value: string }) =>
                setIsActive(selected?.value)
              }
            />
          </div>
        </div>
        <div className="sm:col-span-4 flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
          <div className="flex-1">
            <label
              htmlFor="rule_id"
              className="block text-sm font-medium leading-6"
            >
              {translate("condition-name")}
            </label>
            <div className="mt-2">
              <Select
                options={conditionIds}
                isLoading={isFetchingConditionIds}
                isClearable={true}
                key={`conditionId__${eventId ?? "all"}`}
                id="rule_id"
                placeholder={translate("select-condition")}
                className="py-0.5"
                value={conditionIds.find((d) => d.value === conditionId)}
                onChange={(selected: { label: string; value: string }) =>
                  setConditionId(selected?.value)
                }
              />
            </div>
          </div>
          <div className="shrink-0">
            <label
              htmlFor="search"
              className="block text-sm font-medium leading-6"
            >
              <span className="sr-only">Search</span>
            </label>
            <div className="mt-2 w-full">
              <Button
                type="submit"
                className="w-full bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
                onClick={_handleSubmitFilter}
              >
                <SearchIcon className="w-3 h-4 text-brand-500 mr-2" /> {translate("search")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
